import { sortHand, type Card } from '@cardengine/shared';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useMemo } from 'react';
import { useStore } from '../store.js';
import { PlayingCard } from './PlayingCard.js';

export const HAND_DROP_ID = 'your-hand';

interface HandCardProps {
  card: Card;
  /** Can be picked up and tapped at all. */
  interactive: boolean;
  /** Shown as legal, when the player has asked for that help. */
  highlighted: boolean;
  dimmed: boolean;
  selected: boolean;
  index: number;
  total: number;
  onActivate: (cardId: string) => void;
}

function HandCard({
  card,
  interactive,
  highlighted,
  dimmed,
  selected,
  index,
  total,
  onActivate,
}: HandCardProps) {
  // No transform on the source: a DragOverlay draws the card that follows the
  // cursor, so moving this one too would drag a second ghost along with it.
  // Left in place and faded, it reads as the gap the card came from.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    disabled: !interactive,
  });

  // Fan the hand out: a gentle arc that overlaps more tightly as it grows, so
  // a 13-card hand still fits on a phone.
  const overlap = Math.min(0.62, 0.12 + total * 0.038);
  const offset = index - (total - 1) / 2;
  const rotation = total > 1 ? offset * Math.min(3, 24 / total) : 0;
  const lift = Math.abs(offset) * 1.5;

  const style = {
    marginLeft: index === 0 ? 0 : `calc(var(--card-w) * -${overlap})`,
    zIndex: isDragging ? 999 : index,
    '--fan-rotate': `${rotation}deg`,
    '--fan-lift': `${lift}px`,
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      className={`hand__slot ${isDragging ? 'hand__slot--dragging' : ''}`}
      style={style}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={interactive ? 0 : -1}
      aria-pressed={selected}
      aria-disabled={!interactive}
      onClick={() => interactive && onActivate(card.id)}
      onKeyDown={(event) => {
        if (!interactive) return;
        if (event.key === 'Enter') {
          event.preventDefault();
          onActivate(card.id);
        }
      }}
    >
      <PlayingCard card={card} playable={highlighted} selected={selected} dimmed={dimmed} />
    </div>
  );
}

export function Hand() {
  const game = useStore((s) => s.game);
  const selectedCardIds = useStore((s) => s.selectedCardIds);
  const toggleCard = useStore((s) => s.toggleCard);
  const highlightPlayable = useStore((s) => s.highlightPlayable);

  // Dropping the draw pile here is how you take a card.
  const { setNodeRef, isOver } = useDroppable({ id: HAND_DROP_ID });

  const cards = useMemo(() => (game ? sortHand(game.yourHand) : []), [game]);

  /**
   * Cards that would be accepted right now.
   *
   * With nothing selected this is the set of legal *leads*. Once a lead is
   * chosen, its same-rank companions join it — they are not legal on their own,
   * so otherwise there would be no way to lay more than one card.
   */
  const legalIds = useMemo(() => {
    const ids = new Set<string>();
    for (const move of game?.yourLegalMoves ?? []) {
      if (move.kind === 'play' && move.cardIds[0]) ids.add(move.cardIds[0]);
    }

    const lead = selectedCardIds[0];
    if (!lead) return ids;

    // Reuse the engine's own answer, which already knows the rules about not
    // bundling a blocking card with a raise.
    const group = game?.yourLegalMoves.find(
      (move) => move.kind === 'play' && move.cardIds[0] === lead && move.cardIds.length > 1,
    );
    if (group?.kind === 'play') {
      for (const id of group.cardIds) ids.add(id);
    }
    return ids;
  }, [game, selectedCardIds]);

  if (!game) return null;

  return (
    <div
      ref={setNodeRef}
      className={`hand ${isOver ? 'hand--drop-target' : ''}`}
      aria-label="Your hand"
    >
      {cards.length === 0 ? (
        <p className="hand__empty">No cards left.</p>
      ) : (
        cards.map((card, index) => {
          const legal = legalIds.has(card.id);
          return (
            <HandCard
              key={card.id}
              card={card}
              index={index}
              total={cards.length}
              // Without highlighting, every card can be picked up — trying one
              // and being told why it does not go is part of the game.
              interactive={highlightPlayable ? legal : true}
              highlighted={highlightPlayable && legal}
              dimmed={highlightPlayable && !legal}
              selected={selectedCardIds.includes(card.id)}
              onActivate={toggleCard}
            />
          );
        })
      )}
    </div>
  );
}
